import { formatNumber } from "~/ui-rtk/utils/format"
import {
  MIN_DAILY_BUDGET,
  MAX_DAILY_BUDGET,
  LARGE_CHANGE_PCT,
  UNDER_DELIVERY_RATIO,
  type ApplyResult,
  type GoogleBiddingInfo,
  type RecentChange,
  type RecommendationRow,
  type RowLock,
} from "./types"
import {
  autoSelectPeriod,
  confidenceFor,
  daysSince,
  deliveryRatioFor,
  deriveGoogleBidding,
  formatImpressionShare,
  formatSearchImpressionShare,
  normalizePlatformStatus,
} from "~/shared/budget-recommender/row-utils"

export {
  autoSelectPeriod,
  confidenceFor,
  daysSince,
  deliveryRatioFor,
  deriveGoogleBidding,
  formatImpressionShare,
  formatSearchImpressionShare,
  normalizePlatformStatus,
}

// ── Delivery status ────────────────────────────────────────────────────

/** The row is not spending to its daily budget (see UNDER_DELIVERY_RATIO). */
export function isUnderDelivering(
  row: Pick<RecommendationRow, "deliveryRatio">,
): boolean {
  return row.deliveryRatio !== null && row.deliveryRatio < UNDER_DELIVERY_RATIO
}

/** Tooltip for an under-delivering row: what it spends against what it is allowed to. */
export function describeDelivery(
  row: Pick<
    RecommendationRow,
    "deliveryRatio" | "currentDailyBudget" | "analysisPeriodDays"
  >,
): string {
  if (row.deliveryRatio === null) return ""
  const perDay = row.deliveryRatio * row.currentDailyBudget
  return (
    `Not spending to budget: ${formatNumber(perDay, { currency: true })}/day ` +
    `of its ${formatNumber(row.currentDailyBudget, { currency: true })} daily budget ` +
    `(${Math.round(row.deliveryRatio * 100)}%) over the last ${row.analysisPeriodDays}d. ` +
    "The allocation counts it at its budget; actual spend is likely to be lower."
  )
}

// ── Recent changes (the label that replaced the manual change hold) ────

export function hasRecentChange(
  row: Pick<RecommendationRow, "recentChanges">,
): boolean {
  return (row.recentChanges?.length ?? 0) > 0
}

/** The newest change on the row, for a single compact badge. */
export function primaryRecentChange(
  row: Pick<RecommendationRow, "recentChanges">,
): RecentChange | undefined {
  return row.recentChanges?.[0]
}

/** Short badge text: "Changed 2d" / "Bid 1d" / "Enabled 1d" / "New 3d". */
export function recentChangeBadge(change: RecentChange): string {
  const when = `${change.daysAgo}d`
  switch (change.reason) {
    case "budget":
      return `Changed ${when}`
    case "bid-target":
      return `Bid ${when}`
    case "enabled":
      return `Enabled ${when}`
    case "first-spend":
      return `New ${when}`
  }
}

/** "$200 → $220 (+10%)" when the audit recorded both sides, else "". */
export function formatBudgetDelta(change: RecentChange): string {
  const { previousDailyBudget: prev, newDailyBudget: next } = change
  if (prev === undefined || next === undefined) return ""
  const pct = prev > 0 ? ((next - prev) / prev) * 100 : null
  const pctLabel =
    pct === null ? "" : ` (${pct >= 0 ? "+" : ""}${pct.toFixed(0)}%)`
  return (
    `${formatNumber(prev, { currency: true })} → ` +
    `${formatNumber(next, { currency: true })}${pctLabel}`
  )
}

/**
 * One sentence for tooltips, the acknowledgment dialog and the audit:
 * what changed (with the size of a budget change), when, by whom, and the
 * audit that proves it.
 */
export function describeRecentChange(change: RecentChange): string {
  const when = `${change.daysAgo}d ago`
  const who = change.actor ? ` by ${change.actor}` : ""
  const audit = change.auditId ? ` (audit ${change.auditId})` : ""
  switch (change.reason) {
    case "budget": {
      const delta = formatBudgetDelta(change)
      return `Budget changed${delta ? ` ${delta}` : ""} ${when}${who}${audit}`
    }
    case "bid-target":
      return `Bid target changed ${when}${who}${audit}`
    case "enabled":
      return `Enabled ${when}${who}${audit}`
    case "first-spend":
      return `First spend in our data on ${change.at} (${when})`
  }
}

// ── Pins, the brand-search policy and the step cap (APP 539) ───────────

const formatLockDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })

/** The badge text for a locked row: which rule holds it. */
export function lockBadge(lock: RowLock): string {
  return lock.source === "pin" ? "Pinned" : "Brand policy"
}

/** One sentence for the lock badge's tooltip: why, by whom, and until when. */
export function describeLock(lock: RowLock): string {
  if (lock.source === "brand-policy") {
    return (
      "Held at its current budget by the brand-search policy (Financial Settings → Brand search: Fixed). " +
      "Set the policy to Optimize to let the recommender move brand search."
    )
  }
  const parts = [`Pinned: ${lock.reason ?? "no reason given"}`]
  if (lock.pinnedBy) parts.push(`by ${lock.pinnedBy}`)
  if (lock.pinnedAt) parts.push(`on ${formatLockDate(lock.pinnedAt)}`)
  parts.push(
    lock.expiresAt
      ? `until ${formatLockDate(lock.expiresAt)}`
      : "until unpinned",
  )
  return `${parts.join(" ")}. Held at its current budget and never paused; change the budget by direct edit and the pin holds the new value.`
}

/** Google reports the campaign is limited by its budget (BUDGET_CONSTRAINED). */
export function isLimitedByBudget(
  row: Pick<RecommendationRow, "googleBidding">,
): boolean {
  return row.googleBidding?.constraint === "budget"
}

const LIMITED_BY_BUDGET_TITLE =
  "Google reports this campaign is limited by its budget (BUDGET_CONSTRAINED): it could spend more at its current targets"

/**
 * Tooltip for the "Limited by budget" badge: Google's diagnosis, and — when
 * the live read carried it — the campaign's Search impression share over the
 * read's window, so the badge says how much delivery the budget is costing.
 */
export function limitedByBudgetTitle(
  row: Pick<RecommendationRow, "googleBidding">,
): string {
  const shares = formatSearchImpressionShare(row.googleBidding)
  if (shares === null) return LIMITED_BY_BUDGET_TITLE
  const window = row.googleBidding?.impressionShareWindow
  const period = window
    ? `${window.since} to ${window.until}`
    : "trailing complete days"
  return `${LIMITED_BY_BUDGET_TITLE} · ${shares} (Search impression share, ${period})`
}

/** Tooltip for a step-capped row: what the lane scale gave and what the cap allows. */
export function describeStepCap(
  row: Pick<
    RecommendationRow,
    "stepCapPct" | "unclampedBudget" | "currentDailyBudget"
  >,
): string {
  const pct = Math.round((row.stepCapPct ?? 0) * 100)
  const unclamped = formatNumber(row.unclampedBudget ?? 0, { currency: true })
  return `Step cap ±${pct}%: the recommendation was ${unclamped}; one run may move this row at most ${pct}% from ${formatNumber(row.currentDailyBudget, { currency: true })}.`
}

/**
 * Validate a manually-entered / to-be-applied daily budget ($). Returns an
 * error message, or null when the value is safe to write. Single source of
 * truth shared by the edit input and the apply-time safety gate so the two
 * can never disagree on what a valid budget is.
 */
export function validateDailyBudget(dollars: number): string | null {
  if (!Number.isFinite(dollars)) return "Enter a number"
  if (dollars < MIN_DAILY_BUDGET) return `Min $${MIN_DAILY_BUDGET}`
  if (dollars > MAX_DAILY_BUDGET) {
    return `Max $${MAX_DAILY_BUDGET.toLocaleString()}`
  }
  return null
}

/**
 * Whether a budget change is large enough (vs the current budget) to require an
 * explicit confirmation. No baseline (current <= 0) → not flagged, since a
 * percent swing is undefined.
 */
export function isLargeSwing(
  current: number,
  next: number,
  thresholdPct: number = LARGE_CHANGE_PCT,
): boolean {
  if (current <= 0) return false
  return Math.abs((next - current) / current) * 100 > thresholdPct
}

/**
 * Drop the hand-edited budgets of rows whose approval succeeded. Once applied,
 * an edit is the platform's live budget rather than a pending change — left in
 * place it keeps the row highlighted as edited and would re-send the same value
 * on the next approve. Failed rows keep their edit so the user can retry as-is,
 * and rows not in `results` (manual-only channels) are untouched. Returns the
 * same Map instance when nothing was applied, so callers can skip a re-render.
 */
export function pruneAppliedOverrides(
  overrides: Map<string, number>,
  results: ApplyResult[],
): Map<string, number> {
  const applied = results.filter(r => r.success && overrides.has(r.entityId))
  if (applied.length === 0) return overrides
  const next = new Map(overrides)
  for (const r of applied) next.delete(r.entityId)
  return next
}

/**
 * A row is eligible for a combined budget+tROAS move when it's a Google
 * campaign whose budget is being RAISED while Google says the bid target is
 * the binding constraint. Learning and portfolio rows stay budget-only (the
 * suggestion endpoint also reports these as blockers server-side).
 */
export function isTroasScaleCandidate(row: {
  platform: string | null
  entityType: string
  shouldPause: boolean
  currentDailyBudget: number
  googleBidding?: GoogleBiddingInfo
}): boolean {
  const bidding = row.googleBidding
  return (
    row.platform === "google" &&
    row.entityType === "campaign" &&
    !row.shouldPause &&
    bidding !== undefined &&
    bidding.constraint === "target" &&
    bidding.targetRoas !== undefined &&
    !bidding.learning &&
    !bidding.portfolio &&
    row.currentDailyBudget > 0
  )
}

/**
 * Rows the zero-spend filter keeps: spend > 0 over the row's OWN analysis
 * window (periodSpend is already aggregated per row), so a row hidden at 7d
 * can reappear when its period is widened to a window where it did spend.
 */
export function hasInPeriodSpend(
  row: Pick<RecommendationRow, "periodSpend">,
): boolean {
  return row.periodSpend > 0
}

/**
 * The ids in `eligible` that have never been eligible before. A row that
 * becomes eligible after the page loaded — it just got its initiative type,
 * or a widened window found spend — joins the selection the way it would
 * have on load; a row seen before keeps whatever the user decided for it.
 */
export function newlyEligibleIds(
  eligible: readonly string[],
  seen: ReadonlySet<string>,
): string[] {
  return eligible.filter(id => !seen.has(id))
}

export interface RowsSummary {
  totalAvgDaily: number
  totalCurrent: number
  totalRawRecommended: number
  totalAdjusted: number
  /** (totalAdjusted - totalCurrent) / totalCurrent, in percent; 0 with no baseline */
  aggPctChange: number
}

type SummarizableRow = Pick<
  RecommendationRow,
  | "entityId"
  | "category"
  | "avgDailySpend"
  | "currentDailyBudget"
  | "recommendedBudget"
  | "adjustedBudget"
>

/**
 * Column totals over the assigned (categorized) rows given, honoring inline
 * budget edits. Shared by the table footer and the always-visible totals bar
 * so the two can never disagree; callers pass the rows currently shown, so a
 * filtered view totals what the user sees.
 */
export function summarizeRows(
  rows: readonly SummarizableRow[],
  editOverrides: ReadonlyMap<string, number>,
): RowsSummary {
  const assigned = rows.filter(r => r.category !== null)
  const totalAvgDaily = assigned.reduce((s, r) => s + r.avgDailySpend, 0)
  const totalCurrent = assigned.reduce((s, r) => s + r.currentDailyBudget, 0)
  const totalRawRecommended = assigned.reduce(
    (s, r) => s + r.recommendedBudget,
    0,
  )
  const totalAdjusted = assigned.reduce(
    (s, r) => s + (editOverrides.get(r.entityId) ?? r.adjustedBudget),
    0,
  )
  const aggPctChange =
    totalCurrent > 0 ? ((totalAdjusted - totalCurrent) / totalCurrent) * 100 : 0
  return {
    totalAvgDaily,
    totalCurrent,
    totalRawRecommended,
    totalAdjusted,
    aggPctChange,
  }
}
